import React, { useState } from 'react';
import clsx from 'clsx';
import { WModal } from 'shared_components/components/WModal/WModal';
import WButton from 'shared_components/components/WForms/WButton/WButton';
import { useApi } from 'shared_components/context';
import type { RecordingWestburyContributionsInner } from 'shared_components/generated/admin';
import {
  ContributionDetailModal,
  type ContributionAliasOption,
  type ContributionSavePayload,
} from './ContributionDetailModal';
import { ArtistEditorModal, type ArtistEntry } from './ArtistEditorModal';
import {
  ArtistSearchDropdown,
  ArtistActionModal,
  FeaturedStatusModal,
  type SelectedArtist,
} from './artistEditing';
import { ArtistChip, ContributionChip, ValueChip } from './manualChangeChip';
import type { Row } from '../MasterDiscographyTypes';
import {
  seedArtistsFromRow,
  type MassComplexField,
  type MassFieldValue,
} from '../massEditModel';
import {
  liveToString,
  findDuplicate,
  isValidIsrcFormat,
  ISRC_FORMAT_ERROR,
  isWriteOnceFieldEditable,
  describeDurationError,
  DURATION_FORMAT_HINT,
} from '../../../utils/recordingValidators';
import {
  FIELD_ERROR_BORDER_CLASS,
  FIELD_ERROR_CLASS,
  FIELD_HELP_CLASS,
  FIELD_HINT_CLASS,
  FIELD_INPUT_CLASS,
  FIELD_LABEL_CLASS,
  FIELD_LOCKED_CLASS,
  MODAL_ACTIONS_CLASS,
  SearchSelect as SharedSearchSelect,
} from './formStyles';

export { isValidIsrcFormat };

/**
 * Per-recording "quick edit" modals for the mass-editing prototype.
 *
 * Every editable recording-level field opens one of these rather than editing
 * in place: the complex ones (Live, Version, Contributions, Artists) don't fit
 * in a cell at all, and the simple ones (Title, Duration) have rules a cell has
 * no room to explain. Each is seeded with the recording's *current* value (or
 * the pending
 * mass-edit value if one already exists) and reuses the recording edit form's
 * leaf modals (ContributionDetailModal, ArtistEditorModal) so the business
 * rules stay identical. On save they hand the new value back to the grid, which
 * stores it as a pending edit — nothing is persisted until the user confirms
 * the mass-edit summary.
 */

/* The mass-edit data model and the row → ArtistEntry[] projection live in
 * ../massEditModel so the save layer can use them without pulling in React.
 * They are re-exported here because this module is where callers already
 * import them from. */
export { seedArtistsFromRow } from '../massEditModel';
export type {
  MassComplexField,
  MassLiveValue,
  MassVersionValue,
  MassIsrcValue,
  MassContributionsValue,
  MassArtistsValue,
  MassFieldValue,
  MassRowEdits,
  MassEdits,
} from '../massEditModel';

/* ------------------------------------------------------------------------- */
/* Shared helpers / styling                                                   */
/* ------------------------------------------------------------------------- */

const capitalize = (s: string) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

/* Field presentation comes from ./formStyles, shared with the recording edit
 * form and the bulk-edit builder. Aliased because this module already refers to
 * the input skin by this name throughout. */
const inputClasses = FIELD_INPUT_CLASS;

/** Parse an evidence/notes payload out of a contribution's confidencePattern. */
function parseEvidenceNotes(confidencePattern?: string): {
  evidenceUrl: string;
  notes: string;
} {
  if (!confidencePattern?.trim()) return { evidenceUrl: '', notes: '' };
  try {
    const o = JSON.parse(confidencePattern) as unknown;
    if (typeof o === 'object' && o && !Array.isArray(o)) {
      const obj = o as Record<string, unknown>;
      return {
        evidenceUrl: typeof obj.evidenceUrl === 'string' ? obj.evidenceUrl : '',
        notes: typeof obj.notes === 'string' ? obj.notes : '',
      };
    }
  } catch {
    // ignore malformed patterns
  }
  return { evidenceUrl: '', notes: '' };
}

/** Seed a contributions list from a recording (normalised shape).
 *  `personaId` (the contribution's alias) is an ad-hoc property the backend
 *  carries on each contribution; it is preserved here so that re-editing an
 *  existing server-sourced contribution starts with its alias populated rather
 *  than silently clearing it (mirrors the edit form). */
export function seedContributionsFromRow(
  row: Row
): RecordingWestburyContributionsInner[] {
  return (row.contributions ?? []).map(
    (c) =>
      ({
        contribution: c.contribution,
        confidenceCategory: c.confidenceCategory ?? '1',
        confidencePattern: c.confidencePattern ?? '',
        personaId: (c as { personaId?: string }).personaId,
      }) as RecordingWestburyContributionsInner
  );
}

/** Searchable role picker. The panel, the options and the input skin all come
 *  from ./formStyles, so this reads the same as the equivalent control in the
 *  recording edit form and the bulk-edit builder; only the capitalisation of
 *  role names is local. */
const SearchSelect: React.FC<{
  options: string[];
  exclude?: string[];
  onSelect: (value: string) => void;
  placeholder?: string;
}> = (props) => <SharedSearchSelect {...props} renderOption={capitalize} />;

/* ------------------------------------------------------------------------- */
/* Recording title                                                           */
/* ------------------------------------------------------------------------- */

/**
 * Recording Title gets its own modal rather than an inline cell input, for the
 * same reason Duration does: a grid cell has no room to say what went wrong.
 * The rule is short — a recording must always have a title, so it can be
 * corrected but never cleared — and the modal states it up front instead of
 * silently refusing to commit an empty cell.
 */
export const MassTitleEditModal: React.FC<{
  /** The recording's current title, used to name it in the copy. */
  recordingTitle: string;
  initial: string;
  onClose: () => void;
  onSave: (title: string) => void;
}> = ({ recordingTitle, initial, onClose, onSave }) => {
  const [value, setValue] = useState(initial);
  // Stay quiet until the user has typed — an untouched field shouldn't be
  // scolded just for being open (mirrors the Duration modal).
  const [touched, setTouched] = useState(false);
  const trimmed = value.trim();
  const showError = touched && trimmed.length === 0;

  return (
    <WModal
      isOpen
      onClose={onClose}
      title="Edit Recording Title"
      modalClasses="tw-w-[520px] tw-max-w-[95vw]"
    >
      <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-4">
        <p className={FIELD_HELP_CLASS}>
          Edit the title of “{recordingTitle}”. Every recording needs a title,
          so it can be corrected here but not cleared.
        </p>
        <div className="tw-flex tw-flex-col tw-gap-2">
          <span className={FIELD_LABEL_CLASS}>Recording title</span>
          <input
            className={clsx(
              inputClasses,
              showError && FIELD_ERROR_BORDER_CLASS
            )}
            // Focus via ref rather than autoFocus (jsx-a11y/no-autofocus).
            ref={(el) => el?.focus()}
            value={value}
            placeholder="Recording title"
            onChange={(e) => {
              setValue(e.target.value);
              setTouched(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && trimmed.length > 0) {
                e.preventDefault();
                onSave(trimmed);
              }
            }}
          />
          {showError && (
            <p className={FIELD_ERROR_CLASS}>
              A recording title is required — type the corrected title, or
              cancel to leave it as it was.
            </p>
          )}
        </div>
        <div className={MODAL_ACTIONS_CLASS}>
          <WButton variant="secondary" label="Cancel" onClick={onClose} />
          <WButton
            variant="primary"
            label="Save"
            disabled={trimmed.length === 0}
            onClick={() => onSave(trimmed)}
          />
        </div>
      </div>
    </WModal>
  );
};

/* ------------------------------------------------------------------------- */
/* Duration                                                                  */
/* ------------------------------------------------------------------------- */

/** Tooltip for the disabled Duration edit affordance in the grid. */
export const DURATION_LOCKED_REASON =
  'This duration was set automatically and hasn’t been manually changed, so it can’t be edited here.';

/**
 * Duration gets its own modal for the same reason Title does, only more so:
 * HH:MM:SS is easy to get wrong (people type "3:45"), and a cell has no room to
 * say what's actually wrong. Here the format is stated up front, the value is
 * checked as it's typed, and the message names the specific mistake.
 */
export const MassDurationEditModal: React.FC<{
  recordingTitle: string;
  initial: string;
  /** True when an already-set duration is locked because it was
   *  system-populated and never manually edited (write-once gate). */
  locked?: boolean;
  onClose: () => void;
  onSave: (duration: string) => void;
}> = ({ recordingTitle, initial, locked = false, onClose, onSave }) => {
  const [value, setValue] = useState(initial);
  // Validate as the user types, but stay quiet until they've typed something —
  // a half-entered "00:0" shouldn't be scolded on the first keystroke.
  const [touched, setTouched] = useState(false);
  const error = describeDurationError(value);
  const showError = touched && error !== null;
  const trimmed = value.trim();

  return (
    <WModal
      isOpen
      onClose={onClose}
      title="Edit Duration"
      modalClasses="tw-w-[520px] tw-max-w-[95vw]"
    >
      <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-4">
        {locked ? (
          <p className={FIELD_HELP_CLASS}>
            “{recordingTitle}” already has a duration that was set automatically
            and hasn’t been manually changed. As on the recording edit form, an
            existing duration can only be edited once it has been manually
            changed before, so it’s locked here.
          </p>
        ) : (
          <p className={FIELD_HELP_CLASS}>
            Set the duration for “{recordingTitle}”. Use{' '}
            <span className="tw-font-medium tw-text-white">HH:MM:SS</span> — two
            digits for each part. A 3 minute 45 second recording is{' '}
            <span className="tw-font-medium tw-text-white">00:03:45</span>.
          </p>
        )}
        <div className="tw-flex tw-flex-col tw-gap-2">
          <span className={FIELD_LABEL_CLASS}>Duration</span>
          <input
            className={clsx(
              inputClasses,
              showError && FIELD_ERROR_BORDER_CLASS,
              locked && FIELD_LOCKED_CLASS
            )}
            // Focus via ref rather than autoFocus (jsx-a11y/no-autofocus).
            ref={(el) => {
              if (!locked) el?.focus();
            }}
            value={value}
            disabled={locked}
            inputMode="numeric"
            placeholder="00:03:45"
            onChange={(e) => {
              setValue(e.target.value);
              setTouched(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !error && !locked) {
                e.preventDefault();
                onSave(trimmed);
              }
            }}
          />
          {showError ? (
            <p className={FIELD_ERROR_CLASS}>{error}</p>
          ) : (
            <p className={FIELD_HINT_CLASS}>
              {trimmed
                ? DURATION_FORMAT_HINT
                : 'Leave empty to leave the duration unset.'}
            </p>
          )}
        </div>
        <div className={MODAL_ACTIONS_CLASS}>
          <WButton variant="secondary" label="Cancel" onClick={onClose} />
          <WButton
            variant="primary"
            label="Save"
            disabled={locked || error !== null}
            onClick={() => onSave(trimmed)}
          />
        </div>
      </div>
    </WModal>
  );
};

/* ------------------------------------------------------------------------- */
/* Live                                                                      */
/* ------------------------------------------------------------------------- */

/** Tooltip for the disabled Live edit affordance in the grid. Says the same
 *  thing as the locked modal copy, in one line. */
export const LIVE_LOCKED_REASON =
  'This live version was set automatically and hasn’t been manually changed, so it can’t be edited here.';

const MassLiveEditModal: React.FC<{
  recordingTitle: string;
  initial: string;
  /** True when an already-set live descriptor is locked because it was
   *  system-populated and never manually edited (write-once gate). */
  locked: boolean;
  onClose: () => void;
  onSave: (descriptor: string) => void;
}> = ({ recordingTitle, initial, locked, onClose, onSave }) => {
  const alreadyLive = initial.trim().length > 0;
  const [descriptor, setDescriptor] = useState(initial);
  return (
    <WModal
      isOpen
      onClose={onClose}
      title={alreadyLive ? 'Update live version' : 'Change to Live'}
      modalClasses="tw-w-[520px] tw-max-w-[95vw]"
    >
      <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-4">
        {locked ? (
          <p className={FIELD_HELP_CLASS}>
            “{recordingTitle}” already has a live descriptor that was set
            automatically and hasn’t been manually changed. As on the recording
            edit form, an existing live version can only be edited once it has
            been manually changed before, so it’s locked here.
          </p>
        ) : (
          <p className={FIELD_HELP_CLASS}>
            Mark “{recordingTitle}” as a live recording. A live descriptor is
            required. As on the recording edit form, a recording&apos;s live
            status can be set or updated here but cannot be cleared back to
            not-live.
          </p>
        )}
        <div className="tw-flex tw-flex-col tw-gap-2">
          <span className={FIELD_LABEL_CLASS}>Live descriptor</span>
          <textarea
            className={clsx(
              inputClasses,
              'tw-h-auto tw-resize-y',
              locked && FIELD_LOCKED_CLASS
            )}
            rows={3}
            value={descriptor}
            disabled={locked}
            onChange={(e) => setDescriptor(e.target.value)}
            placeholder="e.g. Live at Wembley, 1985"
          />
        </div>
        <div className={MODAL_ACTIONS_CLASS}>
          <WButton variant="secondary" label="Cancel" onClick={onClose} />
          <WButton
            variant="primary"
            label="Save"
            disabled={locked || descriptor.trim().length === 0}
            onClick={() => onSave(descriptor.trim())}
          />
        </div>
      </div>
    </WModal>
  );
};

/* ------------------------------------------------------------------------- */
/* Version (multi-value list)                                                */
/* ------------------------------------------------------------------------- */

const MassVersionEditModal: React.FC<{
  recordingTitle: string;
  initial: string[];
  onClose: () => void;
  onSave: (versions: string[]) => void;
}> = ({ recordingTitle, initial, onClose, onSave }) => {
  const [versions, setVersions] = useState<string[]>(initial);

  // Mirrors the recording edit form: versions are strictly edit-only here.
  // Existing versions can be renamed or emptied out, but new versions cannot be
  // added (the edit form shows "None" with no add control when a recording has
  // none). A recording with no versions therefore has nothing to edit.
  const hasVersions = initial.length > 0;

  const updateAt = (index: number, value: string) =>
    setVersions((prev) => prev.map((v, i) => (i === index ? value : v)));

  const trimmed = versions.map((v) => v.trim());
  const nonEmpty = trimmed.filter((v) => v.length > 0);
  const duplicate = findDuplicate(nonEmpty);
  const versionsError = duplicate
    ? `“${duplicate}” is listed more than once. Versions must be unique.`
    : null;

  return (
    <WModal
      isOpen
      onClose={onClose}
      title="Edit Version"
      modalClasses="tw-w-[520px] tw-max-w-[95vw]"
    >
      <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-4">
        <p className={FIELD_HELP_CLASS}>
          {hasVersions
            ? `Edit the existing versions for “${recordingTitle}”. As on the recording edit form, versions can be edited but new ones can’t be added here.`
            : `“${recordingTitle}” has no versions. As on the recording edit form, versions can’t be added here.`}
        </p>
        {hasVersions ? (
          <div className="tw-flex tw-flex-col tw-gap-2">
            {versions.map((v, i) => (
              <input
                key={i}
                className={inputClasses}
                value={v}
                onChange={(e) => updateAt(i, e.target.value)}
                onBlur={(e) => updateAt(i, e.target.value.trim())}
                placeholder="Version"
              />
            ))}
          </div>
        ) : (
          <p className="tw-text-sm tw-text-textSeal tw-italic">None</p>
        )}
        {versionsError && <p className={FIELD_ERROR_CLASS}>{versionsError}</p>}
        <div className={MODAL_ACTIONS_CLASS}>
          <WButton variant="secondary" label="Cancel" onClick={onClose} />
          <WButton
            variant="primary"
            label="Save"
            disabled={!hasVersions || !!versionsError}
            onClick={() => onSave(nonEmpty)}
          />
        </div>
      </div>
    </WModal>
  );
};

/* ------------------------------------------------------------------------- */
/* ISRCs (multi-value list with format validation + dedupe)                  */
/* ------------------------------------------------------------------------- */

export interface IsrcDuplicateMatch {
  recordingId: string;
  /** OpenSearch document _id of the source recording — sent back to the API as
   *  part of `dup_isrcs` so the reassign detaches the ISRC from the source
   *  document, not just from the target's list. */
  documentId: string;
  recordingTitle: string;
  artistName: string;
}

/** ISRCs taken from other recordings, keyed by ISRC → the recordings they came
 *  from. Drives both the optimistic grid update and the `dup_isrcs` payload. */
export type IsrcReassignmentMatches = Record<string, IsrcDuplicateMatch[]>;

const MassIsrcEditModal: React.FC<{
  recordingTitle: string;
  initial: string[];
  /** The recording being edited — supplies the pre-edit ISRCs and the
   *  manual-changes overlay behind the chip indicators. */
  row: Row;
  userId?: string;
  parentRecordingId?: string;
  onClose: () => void;
  onSave: (isrcs: string[]) => void;
  /** Reports ISRCs reassigned away from other recordings (isrc → the source
   *  recordings) so the grid can optimistically update those rows and send
   *  `dup_isrcs` when the mass edit is applied. */
  onReassign?: (reassignments: IsrcReassignmentMatches) => void;
}> = ({
  recordingTitle,
  initial,
  row,
  userId,
  parentRecordingId,
  onClose,
  onSave,
  onReassign,
}) => {
  const { adminApi: api } = useApi();
  const [isrcs, setIsrcs] = useState<string[]>(initial);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  // ISRCs reassigned from other recordings this session → the recording ids
  // they were taken from. Drives the "· reassigned" tag and, on save, the
  // optimistic update of those source rows in the grid.
  const [reassignSources, setReassignSources] =
    useState<IsrcReassignmentMatches>({});
  // The ISRC awaiting reassign confirmation, plus the recordings it's on now.
  const [pendingDup, setPendingDup] = useState<{
    isrc: string;
    matches: IsrcDuplicateMatch[];
  } | null>(null);

  // Backend uniqueness check — mirrors the edit form's validateIsrcDuplication.
  const checkDuplicate = async (
    isrc: string
  ): Promise<{ isDuplicate: boolean; matches: IsrcDuplicateMatch[] }> => {
    if (!userId || !parentRecordingId) {
      return { isDuplicate: false, matches: [] };
    }
    try {
      const resp = await api.validateIsrcValidateIsrcResponse({
        userId,
        parentRecordingId,
        validateIsrcRequest: { isrc },
      });
      return {
        isDuplicate: resp.isDuplicate ?? false,
        matches: (resp.matches ?? []).map((m) => ({
          recordingId: m.recordingId ?? '',
          documentId: m.documentId ?? '',
          recordingTitle: m.recordingTitle ?? '',
          artistName: m.artistName ?? '',
        })),
      };
    } catch {
      // On a network/API error, allow the ISRC (don't block on failure).
      return { isDuplicate: false, matches: [] };
    }
  };

  const commitIsrc = (isrc: string, sources?: IsrcDuplicateMatch[]) => {
    setIsrcs((prev) => [...prev, isrc]);
    if (sources && sources.length > 0) {
      setReassignSources((prev) => ({ ...prev, [isrc]: sources }));
    }
    setDraft('');
  };

  const handleSave = () => {
    onSave(isrcs);
    // Only report reassignments for ISRCs still in the final list (the user may
    // have removed a reassigned chip before saving).
    const active = Object.fromEntries(
      Object.entries(reassignSources).filter(([isrc]) => isrcs.includes(isrc))
    );
    if (Object.keys(active).length > 0) {
      onReassign?.(active);
    }
  };

  const addDraft = async () => {
    const raw = draft.trim();
    if (!raw) return;
    if (!isValidIsrcFormat(raw)) {
      setError(ISRC_FORMAT_ERROR);
      return;
    }
    setError(null);
    // Case-insensitive dedupe, mirroring the edit form's addMultipleValue.
    if (isrcs.some((x) => x.toLowerCase() === raw.toLowerCase())) {
      setDraft('');
      return;
    }
    setChecking(true);
    const { isDuplicate, matches } = await checkDuplicate(raw);
    setChecking(false);
    if (isDuplicate) {
      setPendingDup({ isrc: raw, matches });
      return;
    }
    commitIsrc(raw);
  };

  // Chip indicators, matching the recording edit form: purple line for an ISRC
  // added by a server-side manual change, pulsing dot for one added here.
  const manuallyAdded = new Set(row.manuallyAddedIsrcs ?? []);
  const original = new Set(row.isrcsArray ?? []);

  return (
    <>
      <WModal
        isOpen={pendingDup === null}
        onClose={onClose}
        title="Edit ISRCs"
        modalClasses="tw-w-[520px] tw-max-w-[95vw]"
      >
        <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-4">
          <p className={FIELD_HELP_CLASS}>
            Edit the ISRCs for “{recordingTitle}”.
          </p>
          {isrcs.length > 0 && (
            <div className="tw-flex tw-flex-wrap tw-gap-1.5">
              {isrcs.map((v) => (
                <ValueChip
                  key={v}
                  value={v}
                  edited={manuallyAdded.has(v)}
                  changed={!original.has(v)}
                  suffix={
                    reassignSources[v] !== undefined ? (
                      <span className="tw-text-textLime">· reassigned</span>
                    ) : undefined
                  }
                  onRemove={() => {
                    setIsrcs((prev) => prev.filter((x) => x !== v));
                    // Removing a chip clears any stale format error so the
                    // Save button isn't blocked by an error for a gone value.
                    if (error) setError(null);
                  }}
                />
              ))}
            </div>
          )}
          <div className="tw-flex tw-flex-col tw-gap-2">
            <div className="tw-flex tw-items-center tw-gap-2">
              <input
                className={inputClasses}
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                  if (error) setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addDraft();
                  }
                }}
                placeholder="Add an ISRC (e.g. GBAAA0912345)…"
              />
              <WButton
                variant="secondary"
                icon="plus"
                label={checking ? 'Checking…' : 'Add'}
                disabled={draft.trim().length === 0 || checking}
                onClick={addDraft}
              />
            </div>
            {error && <p className={FIELD_ERROR_CLASS}>{error}</p>}
            {draft.trim().length > 0 && !error && (
              <p className="tw-text-yellow-300 tw-text-xs">
                You have an unadded ISRC. Click “Add” to include it before
                saving, or clear the field.
              </p>
            )}
          </div>
          <div className={MODAL_ACTIONS_CLASS}>
            <WButton variant="secondary" label="Cancel" onClick={onClose} />
            <WButton
              variant="primary"
              label="Save"
              // Block save while an ISRC draft is uncommitted so the typed value
              // isn't silently dropped (mirrors the edit form).
              disabled={draft.trim().length > 0 || checking}
              onClick={handleSave}
            />
          </div>
        </div>
      </WModal>

      {/* Reassign confirmation — mirrors the recording edit form's duplicate
          ISRC modal, warning that the other recording(s) lose this ISRC. */}
      <WModal
        isOpen={pendingDup !== null}
        onClose={() => setPendingDup(null)}
        title="Reassign ISRC"
        modalClasses="tw-max-w-md tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4">
          <p className="tw-text-white tw-text-sm tw-mb-3">
            This ISRC is currently assigned to:
          </p>
          {pendingDup && pendingDup.matches.length > 0 && (
            <div className="tw-mb-4 tw-space-y-1.5 tw-max-h-32 tw-overflow-y-auto">
              {pendingDup.matches.map((m, i) => (
                <div
                  key={`${m.recordingTitle}-${i}`}
                  className="tw-flex tw-flex-col tw-bg-surfaceBlack90 tw-rounded tw-px-3 tw-py-2 tw-text-xs"
                >
                  <span className="tw-text-white tw-font-medium">
                    {m.recordingTitle || 'Untitled'}
                  </span>
                  {m.artistName && (
                    <span className="tw-text-textSeal">{m.artistName}</span>
                  )}
                </div>
              ))}
            </div>
          )}
          <p className="tw-text-white tw-text-sm">
            Continuing will remove the ISRC from{' '}
            {pendingDup && pendingDup.matches.length === 1
              ? 'that recording'
              : 'those recordings'}{' '}
            and assign it to{' '}
            <strong>{recordingTitle || 'this recording'}</strong>.
          </p>
          <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-5">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => setPendingDup(null)}
            />
            <WButton
              variant="primary"
              label="Continue"
              onClick={() => {
                if (pendingDup) {
                  commitIsrc(
                    pendingDup.isrc,
                    pendingDup.matches.filter((m) => m.recordingId.length > 0)
                  );
                  setPendingDup(null);
                }
              }}
            />
          </div>
        </div>
      </WModal>
    </>
  );
};

/* ------------------------------------------------------------------------- */
/* Contributions (seeded list; add / edit / remove)                          */
/* ------------------------------------------------------------------------- */

const MassContributionsEditModal: React.FC<{
  recordingTitle: string;
  initial: RecordingWestburyContributionsInner[];
  /** The recording being edited — supplies the pre-edit contributions and the
   *  manual-changes overlay behind the chip indicators. */
  row: Row;
  contributionOptions: string[];
  availableAliases: ContributionAliasOption[];
  onClose: () => void;
  onSave: (entries: RecordingWestburyContributionsInner[]) => void;
}> = ({
  recordingTitle,
  initial,
  row,
  contributionOptions,
  availableAliases,
  onClose,
  onSave,
}) => {
  const [entries, setEntries] =
    useState<RecordingWestburyContributionsInner[]>(initial);
  // The role being added (opens ContributionDetailModal in 'add' mode).
  const [pendingRole, setPendingRole] = useState<string | null>(null);
  // The existing contribution being edited (index into entries).
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  // The contribution pending remove confirmation (index into entries).
  const [removeIndex, setRemoveIndex] = useState<number | null>(null);

  const detailOpen = pendingRole !== null || editingIndex !== null;
  const editingEntry =
    editingIndex !== null ? entries[editingIndex] : undefined;
  const editingNotes = editingEntry
    ? parseEvidenceNotes(editingEntry.confidencePattern)
    : { evidenceUrl: '', notes: '' };

  const closeDetail = () => {
    setPendingRole(null);
    setEditingIndex(null);
  };

  // Chip indicators, matching the recording edit form:
  //  - purple line  → the role carries a manual change recorded server-side
  //  - pulsing dot  → the role was added or altered in this session
  const manuallyChangedRoles = new Set(
    row.manuallyChangedContributionRoles ?? []
  );
  const original = seedContributionsFromRow(row);
  const localChangeFor = (
    c: RecordingWestburyContributionsInner
  ): { changed: boolean; title: string } => {
    const orig = original.find((o) => o.contribution === c.contribution);
    if (!orig) return { changed: true, title: 'Newly added contribution' };
    const changed =
      orig.confidenceCategory !== c.confidenceCategory ||
      orig.confidencePattern !== c.confidencePattern;
    return { changed, title: 'Edited contribution' };
  };

  return (
    <>
      <WModal
        isOpen={!detailOpen && removeIndex === null}
        onClose={onClose}
        title="Edit Contributions"
        modalClasses="tw-w-[560px] tw-max-w-[95vw]"
      >
        <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-4">
          <p className={FIELD_HELP_CLASS}>
            Edit the contributions for “{recordingTitle}”. Click a contribution
            to change its data quality or evidence.
          </p>
          {entries.length > 0 && (
            <div className="tw-flex tw-flex-wrap tw-gap-1.5">
              {entries
                .map((cont, originalIndex) => ({ cont, originalIndex }))
                .sort(
                  (a, b) =>
                    parseInt(a.cont.confidenceCategory ?? '1', 10) -
                    parseInt(b.cont.confidenceCategory ?? '1', 10)
                )
                .map(({ cont, originalIndex }) => {
                  const local = localChangeFor(cont);
                  return (
                    <ContributionChip
                      key={cont.contribution}
                      role={cont.contribution ?? ''}
                      confidenceCategory={cont.confidenceCategory}
                      edited={manuallyChangedRoles.has(cont.contribution ?? '')}
                      changed={local.changed}
                      changedTitle={local.title}
                      onSelect={() => setEditingIndex(originalIndex)}
                      onRemove={
                        entries.length > 1
                          ? () => setRemoveIndex(originalIndex)
                          : undefined
                      }
                    />
                  );
                })}
            </div>
          )}
          <div className="tw-flex tw-flex-col tw-gap-2">
            <span className={FIELD_LABEL_CLASS}>Add a contribution</span>
            <SearchSelect
              options={contributionOptions}
              exclude={entries.map((e) => e.contribution)}
              onSelect={(role) => setPendingRole(role)}
              placeholder="Search or select contribution…"
            />
          </div>
          <div className={MODAL_ACTIONS_CLASS}>
            <WButton variant="secondary" label="Cancel" onClick={onClose} />
            <WButton
              variant="primary"
              label="Save"
              disabled={entries.length === 0}
              onClick={() => onSave(entries)}
            />
          </div>
        </div>
      </WModal>

      {/* Reused contribution detail modal for the role being added/edited. */}
      <ContributionDetailModal
        isOpen={detailOpen}
        mode={editingIndex !== null ? 'edit' : 'add'}
        initialValue={
          editingIndex !== null && editingEntry
            ? {
                contributionName: editingEntry.contribution,
                confidenceCategory: editingEntry.confidenceCategory ?? '1',
                evidenceUrl: editingNotes.evidenceUrl,
                notes: editingNotes.notes,
                personaId:
                  (editingEntry as ContributionSavePayload).personaId ?? '',
              }
            : { contributionName: pendingRole ?? '' }
        }
        aliases={availableAliases}
        onClose={closeDetail}
        onSave={(payload) => {
          setEntries((prev) => {
            if (editingIndex !== null) {
              return prev.map((e, i) => (i === editingIndex ? payload : e));
            }
            return [...prev, payload];
          });
          closeDetail();
        }}
      />

      {/* Remove-contribution confirmation — mirrors the recording edit form. */}
      <WModal
        isOpen={removeIndex !== null}
        onClose={() => setRemoveIndex(null)}
        title="Remove Contribution"
        modalClasses="tw-max-w-sm tw-w-full tw-mx-4"
      >
        <div className="tw-mt-4">
          <p className="tw-text-white tw-text-sm tw-mb-6">
            Are you sure you want to remove{' '}
            <strong>
              {removeIndex !== null
                ? capitalize(entries[removeIndex]?.contribution ?? '')
                : ''}
            </strong>{' '}
            from “{recordingTitle}”?
          </p>
          <div className="tw-flex tw-justify-end tw-gap-2">
            <WButton
              variant="secondary"
              label="Cancel"
              onClick={() => setRemoveIndex(null)}
            />
            <WButton
              variant="primary"
              label="Remove"
              onClick={() => {
                if (removeIndex !== null) {
                  const idx = removeIndex;
                  setEntries((prev) => prev.filter((_, i) => i !== idx));
                  setRemoveIndex(null);
                }
              }}
            />
          </div>
        </div>
      </WModal>
    </>
  );
};

/* ------------------------------------------------------------------------- */
/* Artists (seeded list; add existing / create new / remove)                 */
/* ------------------------------------------------------------------------- */

const MassArtistsEditModal: React.FC<{
  recordingTitle: string;
  initial: ArtistEntry[];
  /** The recording's stored artists, used to tell "already linked" from "added
   *  in this session" when deciding which chips get the manual-change line. */
  row: Row;
  userId?: string;
  parentRecordingId?: string;
  onClose: () => void;
  onSave: (entries: ArtistEntry[]) => void;
}> = ({
  recordingTitle,
  initial,
  row,
  userId,
  parentRecordingId,
  onClose,
  onSave,
}) => {
  // Demoted artists stay in `entries` as tombstones — the save layer needs them
  // to emit the `delete` + `nfa` edit — but they are filtered out of the list
  // below, which only ever shows featured / other-featured artists.
  const [entries, setEntries] = useState<ArtistEntry[]>(initial);
  const [addingArtist, setAddingArtist] = useState(false);
  // An existing artist picked from search, awaiting the featured-status choice.
  const [pendingArtist, setPendingArtist] = useState<SelectedArtist | null>(
    null
  );
  // Identity of the artist whose remove/demote confirmation is open.
  const [actionKey, setActionKey] = useState<string | null>(null);

  const keyOf = (a: { id?: string; name: string }) => a.id || a.name;
  const visible = entries.filter((e) => !e.demoted);
  const actionArtist =
    actionKey === null
      ? null
      : entries.find((e) => keyOf(e) === actionKey) ?? null;

  // Chips carry the purple manual-change line when the artist was added by a
  // previous manual change (4.5.2 overlay) or added locally in this session —
  // the same rule the recording edit form applies.
  const originalKeys = new Set(seedArtistsFromRow(row).map(keyOf));
  const addedIds = new Set(row.manuallyAddedArtistIds ?? []);
  const addedNames = new Set(row.manuallyAddedArtistNames ?? []);
  const isEdited = (a: ArtistEntry) =>
    !originalKeys.has(keyOf(a)) ||
    (!!a.id && addedIds.has(a.id)) ||
    addedNames.has(a.name);

  return (
    <>
      <WModal
        isOpen={!addingArtist && actionKey === null && pendingArtist === null}
        onClose={onClose}
        title="Edit Artists"
        modalClasses="tw-w-[560px] tw-max-w-[95vw]"
      >
        <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-4">
          <p className={FIELD_HELP_CLASS}>
            Edit the artists for “{recordingTitle}”. At least one artist is
            required. As on the recording edit form, removing an existing artist
            offers demote-to-non-featured or full removal; a demoted artist
            leaves this list.
          </p>
          {visible.length > 0 && (
            <div className="tw-flex tw-flex-wrap tw-gap-1.5">
              {visible.map((a) => {
                // Last-artist guard: a recording must keep ≥1 artist.
                const removable = visible.length > 1;
                return (
                  <ArtistChip
                    key={keyOf(a)}
                    name={a.name}
                    edited={isEdited(a)}
                    removeTitle={
                      a.pendingCreate
                        ? 'Remove artist'
                        : 'Demote or remove artist'
                    }
                    onRemove={
                      removable
                        ? () => {
                            if (a.pendingCreate) {
                              // Never linked — just drop it locally.
                              setEntries((prev) =>
                                prev.filter((e) => keyOf(e) !== keyOf(a))
                              );
                            } else {
                              setActionKey(keyOf(a));
                            }
                          }
                        : undefined
                    }
                  />
                );
              })}
            </div>
          )}
          <div className="tw-flex tw-flex-col tw-gap-2">
            <span className={FIELD_LABEL_CLASS}>Search existing artists</span>
            <ArtistSearchDropdown
              userId={userId}
              parentRecordingId={parentRecordingId}
              existing={entries}
              onSelect={(artist) => setPendingArtist(artist)}
            />
          </div>
          <div className="tw-flex tw-justify-start">
            <WButton
              variant="secondary"
              icon="plus"
              label="Create new artist"
              onClick={() => setAddingArtist(true)}
            />
          </div>
          <div className={MODAL_ACTIONS_CLASS}>
            <WButton variant="secondary" label="Cancel" onClick={onClose} />
            <WButton
              variant="primary"
              label="Save"
              disabled={visible.length === 0}
              onClick={() => onSave(entries)}
            />
          </div>
        </div>
      </WModal>

      {/* Demote-vs-remove confirmation for an existing artist. Demote keeps the
          artist linked but non-featured; remove drops it entirely. */}
      <ArtistActionModal
        artistName={actionArtist?.name ?? null}
        onCancel={() => setActionKey(null)}
        onDemote={() => {
          setEntries((prev) =>
            prev.map((e) =>
              keyOf(e) === actionKey
                ? {
                    ...e,
                    isFeatured: false,
                    featuredStatus: 'nf',
                    // Demoted artists drop out of the list (it only shows
                    // featured / other-featured artists) but stay in the value
                    // as tombstones — the flag is the only signal the save
                    // layer has to emit the delete+nfa edit instead of a plain
                    // removal.
                    demoted: true,
                  }
                : e
            )
          );
          setActionKey(null);
        }}
        onRemove={() => {
          setEntries((prev) => prev.filter((e) => keyOf(e) !== actionKey));
          setActionKey(null);
        }}
      />

      {/* Featured-status choice for a just-searched artist (edit-form parity:
          search first, then pick Featured / Other Featured in a second modal). */}
      <FeaturedStatusModal
        pending={pendingArtist}
        onCancel={() => setPendingArtist(null)}
        onAdd={(entry) => {
          setEntries((prev) => {
            const existing = prev.find(
              (e) => e.name === entry.name || (!!e.id && e.id === entry.id)
            );
            // Re-adding an artist that was demoted in this session undoes the
            // demotion rather than being a no-op against the tombstone.
            if (existing) {
              return existing.demoted
                ? prev.map((e) =>
                    e === existing ? { ...e, ...entry, demoted: undefined } : e
                  )
                : prev;
            }
            return [...prev, entry];
          });
          setPendingArtist(null);
        }}
      />

      <ArtistEditorModal
        isOpen={addingArtist}
        userId={userId}
        parentRecordingId={parentRecordingId}
        onClose={() => setAddingArtist(false)}
        onAdd={(entry) => {
          setEntries((prev) => [...prev, entry]);
          setAddingArtist(false);
        }}
      />
    </>
  );
};

/* ------------------------------------------------------------------------- */
/* Router                                                                     */
/* ------------------------------------------------------------------------- */

interface MassEditFieldModalProps {
  field: MassComplexField;
  row: Row;
  /** The current pending value for this field, if one already exists. */
  pending?: MassFieldValue;
  availableContributions: string[];
  availableAliases: ContributionAliasOption[];
  userId?: string;
  onClose: () => void;
  onSave: (field: MassComplexField, value: MassFieldValue) => void;
  /** Optimistically strip a reassigned ISRC from its source recordings. */
  onReassignIsrc?: (reassignments: IsrcReassignmentMatches) => void;
}

export const MassEditFieldModal: React.FC<MassEditFieldModalProps> = ({
  field,
  row,
  pending,
  availableContributions,
  availableAliases,
  userId,
  onClose,
  onSave,
  onReassignIsrc,
}) => {
  const title = row.title || '(untitled)';

  switch (field) {
    case 'live': {
      // Write-once gate: an already-set live descriptor is only editable once
      // it has been manually changed before (row.liveEditedManually). A pending
      // in-session edit keeps it editable so the user can revise their change.
      const liveLocked =
        typeof pending !== 'string' &&
        !isWriteOnceFieldEditable(
          liveToString(row.live),
          !!row.liveEditedManually
        );
      return (
        <MassLiveEditModal
          recordingTitle={title}
          initial={
            typeof pending === 'string' ? pending : liveToString(row.live)
          }
          locked={liveLocked}
          onClose={onClose}
          onSave={(descriptor) => onSave('live', descriptor)}
        />
      );
    }
    case 'version':
      return (
        <MassVersionEditModal
          recordingTitle={title}
          initial={
            Array.isArray(pending)
              ? (pending as string[])
              : row.versionsArray ?? []
          }
          onClose={onClose}
          onSave={(versions) => onSave('version', versions)}
        />
      );
    case 'contributions':
      return (
        <MassContributionsEditModal
          recordingTitle={title}
          initial={
            Array.isArray(pending)
              ? (pending as RecordingWestburyContributionsInner[])
              : seedContributionsFromRow(row)
          }
          row={row}
          contributionOptions={availableContributions}
          availableAliases={availableAliases}
          onClose={onClose}
          onSave={(entries) => onSave('contributions', entries)}
        />
      );
    case 'artist':
      return (
        <MassArtistsEditModal
          recordingTitle={title}
          initial={
            Array.isArray(pending)
              ? (pending as ArtistEntry[])
              : seedArtistsFromRow(row)
          }
          row={row}
          userId={userId}
          parentRecordingId={row.id}
          onClose={onClose}
          onSave={(entries) => onSave('artist', entries)}
        />
      );
    case 'isrcs':
      return (
        <MassIsrcEditModal
          recordingTitle={title}
          initial={
            Array.isArray(pending)
              ? (pending as string[])
              : row.isrcsArray ?? []
          }
          row={row}
          userId={userId}
          parentRecordingId={row.id}
          onClose={onClose}
          onSave={(isrcs) => onSave('isrcs', isrcs)}
          onReassign={onReassignIsrc}
        />
      );
    default:
      return null;
  }
};

export default MassEditFieldModal;
